// Typed client for Services > Channels > Queues — the handoff queues, their
// staffing and their routing config. Every call goes to THIS application's
// server (/api/services/queues/*), which is member-gated for reads,
// tenant-admin gated for writes, and hops to the channels service over S2S. The
// browser never talks to channels directly and never names a tenant.
import { api } from './api'

/** Agent liveness, as channels reports it (the Desk's own presence model). */
export type Presence = 'online' | 'away' | 'busy' | 'offline'

export interface Queue {
  id: string
  key: string
  name: string
  members: number
  offerable: number
}

export interface QueueMember {
  userId: string
  displayName: string | null
  presence: Presence | null
  capacity: number | null
  inflight: number | null
  lastSeenAt: string | null
  createdAt: string
}

export interface AgentDirectoryEntry {
  userId: string
  displayName: string | null
  presence: Presence
  capacity: number
  inflight: number
  lastSeenAt: string
  queues: number
}

// Per-queue offer/availability overrides. GET returns the stored overrides AND
// the platform defaults, so the form shows "inherit" placeholders; PUT sends
// only the fields the admin filled in (empty = revert to inherit).
export interface RoutingConfig {
  offerSeconds?: number
  rotation?: 'sequential' | 'ring_all'
  reRingCycles?: number
  reRingCooldownSeconds?: number
  exhaustionAction?: 'stay_awaiting' | 'voicemail'
  exhaustionVoicemailAfterSeconds?: number
  wrapupGraceSeconds?: number
  noClaimSeconds?: number
  noClaimMessage?: string
  /** Prefer the agents who already handled this customer when ordering the offer
   *  on a new SMS/WhatsApp conversation (#61). Preference only. */
  stickyAgent?: boolean
  /** Close a CLAIMED web chat nobody has touched this long (frees the agent's
   *  slot). 0 disables. */
  chatIdleCloseSeconds?: number
  /** Once the visitor's browser reports the page gone, close after this much
   *  further silence (shorter than the blind idle window). 0 disables. */
  chatVisitorLeftCloseSeconds?: number
}

const BASE = '/api/services/queues'

export const queuesAdmin = {
  list: () => api.get<{ queues: Queue[] }>(BASE).then((r) => r.queues),
  agents: () => api.get<{ agents: AgentDirectoryEntry[] }>(`${BASE}/agents`).then((r) => r.agents),
  members: (id: string) => api.get<{ members: QueueMember[] }>(`${BASE}/${id}/members`).then((r) => r.members),
  impact: (id: string) => api.get<{ waiting: number }>(`${BASE}/${id}/impact`),

  create: (input: { key: string; name: string }) => api.post<Queue>(BASE, input),
  rename: (id: string, name: string) => api.patch<Queue>(`${BASE}/${id}`, { name }),
  remove: (id: string) => api.delete<{ ok: boolean }>(`${BASE}/${id}`),
  addMember: (id: string, userId: string) => api.post<{ ok: boolean }>(`${BASE}/${id}/members`, { userId }),
  removeMember: (id: string, userId: string) => api.delete<{ ok: boolean }>(`${BASE}/${id}/members/${userId}`),

  getRouting: (id: string) => api.get<{ routing: RoutingConfig; defaults: Required<RoutingConfig> }>(`${BASE}/${id}/routing`),
  putRouting: (id: string, body: RoutingConfig) => api.put<{ routing: RoutingConfig }>(`${BASE}/${id}/routing`, body),

  // Workforce admin: an agent's open claimed load, their capacity, and the
  // admin force-close lever for a stuck conversation.
  agentConversations: (userId: string) =>
    api.get<{ conversations: AgentConversation[] }>(`${BASE}/agents/${userId}/conversations`).then((r) => r.conversations),
  setAgentCapacity: (userId: string, capacity: number) =>
    api.put<AgentDirectoryEntry>(`${BASE}/agents/${userId}/capacity`, { capacity }),
  adminCloseConversation: (conversationId: string, reason?: string) =>
    api.post<{ ok: boolean }>(`${BASE}/conversations/${conversationId}/close`, reason ? { reason } : {}),
}

/** One of an agent's open claimed conversations — what pins an inflight slot. */
export interface AgentConversation {
  conversationId: string
  channel: string
  handoffState: string | null
  openedAt: string
  updatedAt: string
}
